<script setup lang="ts">
import { computed, ref } from 'vue';

import StSidebarNavItem from './StSidebarNavItem.vue';
import { UrlHelper } from './Helpers/url-helper';
import type { AppStudioPageProps } from './Vue3/app-studio-page-props';

type RouteData = Record<string, string>;

interface SidebarModel {
    title?: string;
    appIdentifier?: string;
    fieldTypeNames?: string;
    hasHomepagesAccess?: boolean;
    hasDashboardsAccess?: boolean;
    hasTemplatedReportsAccess?: boolean;
    hasMaintainLists?: boolean;
    hasStylesAccess?: boolean;
    hasMaintainAssetsAccess?: boolean;
    hasSiteConfigureAccess?: boolean;
    hasMcpConfigureAccess?: boolean;
    hasAppsAccess?: boolean;
    hasConfigureAdvancedAreaAccess?: boolean;
    hasAppsEditAccess?: boolean;
    hasFieldsAccess?: boolean;
    hasTemplatesAccess?: boolean;
    hasFormsAccess?: boolean;
    hasAdvancedFiltersAccess?: boolean;
    hasReportsAccess?: boolean;
    hasReportsAdvancedAccess?: boolean;
    hasRulesAccess?: boolean;
    hasTranslationsAccess?: boolean;
    hasWorkflowItemsAccess?: boolean;
    hasWorkflowRunsAllSitesAccess?: boolean;
    hasIntegrationsAccess?: boolean;
    appsCount?: number;
    homepagesCount?: number;
    dashboardsCount?: number;
    templatedReportsCount?: number;
    selectListsCount?: number;
    imageListsCount?: number;
    stylesCount?: number;
    iconsCount?: number;
    fieldsCount?: number;
    templatesCount?: number;
    formsCount?: number;
    cardReportsCount?: number;
    chartReportsCount?: number;
    dashboardReportsCount?: number;
    listAndTableReportsCount?: number;
    matrixReportsCount?: number;
    viewReportsCount?: number;
    rulesCount?: number;
    workflowV2Count?: number;
    integrateCount?: number;
    asideMenuListReportRouteData?: RouteData;
    asideMenuMatrixReportRouteData?: RouteData;
    asideMenuChartReportRouteData?: RouteData;
    asideMenuDashboardReportRouteData?: RouteData;
    asideMenuCardReportRouteData?: RouteData;
    asideMenuViewReportRouteData?: RouteData;
    asideMenuWorkflowRunsRouteData?: RouteData;
    asideMenuWorkflowRunsAllSitesRouteData?: RouteData;
}

interface SidebarOptions {
    variant: 'app' | 'main';
}

type SidebarPageProps = AppStudioPageProps<SidebarModel, SidebarOptions>;

defineOptions({ name: 'app-sidebar-vue' });
const props = defineProps<{ pageProps: SidebarPageProps }>();

const model = computed(() => props.pageProps.model);
const urls = computed(() => props.pageProps.urls);
const currentController = (window.softools.controller?.toString() ?? '').toLowerCase();
const currentSearchParams = new URLSearchParams(window.location.search);

function routeMatches(controllers: string[], routeData?: RouteData): boolean {
    if (!controllers.some((controller) => controller.toLowerCase() === currentController)) return false;
    if (!routeData) return true;

    return Object.entries(routeData).every(([key, value]) => {
        const matchingKey = Array.from(currentSearchParams.keys()).find((queryKey) => queryKey.toLowerCase() === key.toLowerCase());
        return matchingKey !== undefined && currentSearchParams.getAll(matchingKey).every((queryValue) => queryValue === value);
    });
}

const fieldControllers = computed(() => ['Fields', 'Templates', 'Forms', ...(model.value.fieldTypeNames?.split(',') ?? [])]);
const isAllSitesWorkflowRuns = routeMatches(['WorkflowRuns'], model.value.asideMenuWorkflowRunsAllSitesRouteData);
const appGuideCollapsed = ref(!routeMatches(['Configure', 'ConfigureAdvanced', 'AppGuide']));
const configureCollapsed = ref(!routeMatches(['Configure']));
const isMcpConfiguration = /\/configure\/mcp\/?$/i.test(window.location.pathname);
const isSsoConfiguration = /\/configure\/sso\/?$/i.test(window.location.pathname);
const hasConfigureChildPages = computed(() => model.value.hasMcpConfigureAccess || model.value.hasWorkflowRunsAllSitesAccess);
const inputAndLayoutCollapsed = ref(!routeMatches(fieldControllers.value));
const reportsCollapsed = ref(!routeMatches(['Reports', 'Filters']));
const publishCollapsed = ref(!routeMatches(['Publish', 'Security']));
const advancedCollapsed = ref(
    !routeMatches(['WorkflowV2', 'Rules', 'Integrate', 'ChildAppIntegration', 'ExternalIntegration', 'Translations'])
);
const adminCollapsed = ref(!routeMatches(['Sites', 'AppCopy']) && !isAllSitesWorkflowRuns);

const defaultInputAndLayoutUrl = computed(() =>
    model.value.hasFieldsAccess ? urls.value.fields : model.value.hasTemplatesAccess ? urls.value.templates : urls.value.forms
);
const defaultAdvancedUrl = computed(() =>
    model.value.hasAppsAccess
        ? urls.value.integrate
        : model.value.hasRulesAccess
          ? urls.value.rules
          : model.value.hasWorkflowItemsAccess
            ? urls.value.workflow
            : urls.value.translations
);

function openUrl(url: string): void {
    UrlHelper.onOpenUrlHandler(url);
}
</script>

<template>
    <ul class="nav appstudio-sidebar-nav">
        <template v-if="pageProps.options.variant === 'main'">
            <StSidebarNavItem
                label="My Apps"
                icon="fal fa-th"
                :href="urls.home"
                :active="routeMatches(['Home'])"
                :count="model.appsCount"
            />
            <StSidebarNavItem
                v-if="model.hasHomepagesAccess"
                label="Homepages"
                icon="fal fa-house-flag"
                :href="urls.homepages"
                :active="routeMatches(['Homepages'])"
                :count="model.homepagesCount"
                :create-url="urls.homepagesCreate"
                @open="openUrl"
            />
            <StSidebarNavItem
                v-if="model.hasDashboardsAccess"
                label="Dashboards"
                icon="fal fa-tachometer-alt"
                :href="urls.dashboards"
                :active="routeMatches(['Dashboards'])"
                :count="model.dashboardsCount"
                :create-url="urls.dashboardsCreate"
                @open="openUrl"
            />
            <StSidebarNavItem
                v-if="model.hasTemplatedReportsAccess"
                label="Templated Reports"
                icon="fal fa-chart-bar"
                :href="urls.templatedReports"
                :active="routeMatches(['TemplatedReports'])"
                :count="model.templatedReportsCount"
                :create-url="urls.templatedReportsCreate"
                @open="openUrl"
            />
            <StSidebarNavItem
                v-if="model.hasMaintainLists"
                label="Select Lists"
                icon="fal fa-list"
                :href="urls.selectLists"
                :active="routeMatches(['SelectLists'])"
                :count="model.selectListsCount"
                :create-url="urls.selectListsCreate"
                @open="openUrl"
            />
            <StSidebarNavItem
                v-if="model.hasMaintainLists"
                label="Image Lists"
                icon="fal fa-list"
                :href="urls.imageLists"
                :active="routeMatches(['ImageLists'])"
                :count="model.imageListsCount"
                :create-url="urls.imageListsCreate"
                @open="openUrl"
            />
            <StSidebarNavItem
                v-if="model.hasStylesAccess"
                label="Styles"
                icon="fal fa-palette"
                :href="urls.styles"
                :active="routeMatches(['Styles'])"
                :count="model.stylesCount"
                :create-url="urls.stylesCreate"
                @open="openUrl"
            />
            <StSidebarNavItem
                v-if="model.hasMaintainAssetsAccess"
                label="Icons"
                icon="fal fa-image"
                :href="urls.icons"
                :active="routeMatches(['Icons'])"
                :count="model.iconsCount"
            />
            <StSidebarNavItem
                v-if="model.hasWorkflowItemsAccess"
                label="Workflow Runs"
                icon="fal fa-wrench"
                :href="urls.workflowRuns"
                :active="routeMatches(['WorkflowRuns'], model.asideMenuWorkflowRunsRouteData)"
            />

            <template v-if="model.hasWorkflowRunsAllSitesAccess">
                <StSidebarNavItem
                    label="Admin"
                    icon="fal fa-user-shield"
                    collapsible
                    toggle-on-link
                    :collapsed="adminCollapsed"
                    @toggle="adminCollapsed = !adminCollapsed"
                />
                <template v-if="!adminCollapsed">
                    <StSidebarNavItem
                        label="Workflow Runs (All Sites)"
                        icon="fas fa-wrench"
                        sub-item
                        :href="urls.workflowRunsAllSites"
                        :active="routeMatches(['WorkflowRuns'], model.asideMenuWorkflowRunsAllSitesRouteData)"
                    />
                    <StSidebarNavItem label="Sites" icon="fas fa-globe" sub-item :href="urls.sites" :active="routeMatches(['Sites'])" />
                    <StSidebarNavItem
                        label="App Copy"
                        icon="fas fa-copy"
                        sub-item
                        :href="urls.appCopy"
                        :active="routeMatches(['AppCopy'])"
                    />
                </template>
            </template>

            <StSidebarNavItem
                v-if="model.hasSiteConfigureAccess || hasConfigureChildPages"
                label="Configure"
                icon="fal fa-cog"
                :href="
                    model.hasSiteConfigureAccess ? urls.configureSite : model.hasMcpConfigureAccess ? urls.configureMcp : urls.configureSso
                "
                :active="routeMatches(['Configure'])"
                :collapsible="hasConfigureChildPages"
                :collapsed="configureCollapsed"
                @toggle="configureCollapsed = !configureCollapsed"
            />
            <template v-if="hasConfigureChildPages && !configureCollapsed">
                <StSidebarNavItem
                    v-if="model.hasSiteConfigureAccess"
                    label="Site settings"
                    icon="fas fa-cog"
                    sub-item
                    :href="urls.configureSite"
                    :active="routeMatches(['Configure']) && !isMcpConfiguration && !isSsoConfiguration"
                />
                <StSidebarNavItem
                    v-if="model.hasMcpConfigureAccess"
                    label="MCP connections"
                    icon="fas fa-plug"
                    sub-item
                    :href="urls.configureMcp"
                    :active="routeMatches(['Configure']) && isMcpConfiguration"
                />
                <StSidebarNavItem
                    v-if="model.hasWorkflowRunsAllSitesAccess"
                    label="SSO settings"
                    icon="fas fa-key"
                    sub-item
                    :href="urls.configureSso"
                    :active="routeMatches(['Configure']) && isSsoConfiguration"
                />
            </template>
            <StSidebarNavItem label="Developer" icon="fal fa-code" :href="urls.developer" />
        </template>

        <template v-else>
            <li>
                <h1 class="sidebar-heading text-truncate softools-orange">
                    <span class="lead">{{ model.title }}</span>
                </h1>
            </li>

            <template v-if="model.hasAppsAccess">
                <StSidebarNavItem
                    label="App Configuration"
                    icon="fal fa-cog"
                    :href="urls.appGuide"
                    :active="routeMatches(['AppGuide', 'Configure', 'ConfigureAdvanced'])"
                    collapsible
                    :collapsed="appGuideCollapsed"
                    @toggle="appGuideCollapsed = !appGuideCollapsed"
                />
                <template v-if="!appGuideCollapsed">
                    <StSidebarNavItem
                        label="App Guide"
                        icon="fas fa-file-invoice"
                        sub-item
                        :href="urls.appGuide"
                        :active="routeMatches(['AppGuide'])"
                    />
                    <StSidebarNavItem
                        label="Basic Config"
                        icon="fas fa-cog"
                        sub-item
                        :href="urls.configureApp"
                        :active="routeMatches(['Configure'])"
                    />
                    <StSidebarNavItem
                        v-if="model.hasConfigureAdvancedAreaAccess"
                        label="Advanced Config"
                        icon="fas fa-cog"
                        sub-item
                        :href="urls.configureAdvanced"
                        :active="routeMatches(['ConfigureAdvanced'])"
                    />
                </template>
            </template>

            <template v-if="model.hasFieldsAccess || model.hasTemplatesAccess || model.hasFormsAccess">
                <StSidebarNavItem
                    label="Input & Layout"
                    icon="fal fa-grid-2-plus"
                    :href="defaultInputAndLayoutUrl"
                    :active="routeMatches(fieldControllers)"
                    collapsible
                    :collapsed="inputAndLayoutCollapsed"
                    @toggle="inputAndLayoutCollapsed = !inputAndLayoutCollapsed"
                />
                <template v-if="!inputAndLayoutCollapsed">
                    <StSidebarNavItem
                        v-if="model.hasFieldsAccess"
                        label="Fields"
                        icon="fas fa-edit"
                        sub-item
                        :href="urls.fields"
                        :active="routeMatches(['Fields', ...(model.fieldTypeNames?.split(',') ?? [])])"
                        :count="model.fieldsCount"
                        :create-url="urls.fieldsCreate"
                        @open="openUrl"
                    />
                    <StSidebarNavItem
                        v-if="model.hasTemplatesAccess"
                        label="Templates"
                        icon="fas fa-tasks"
                        sub-item
                        :href="urls.templates"
                        :active="routeMatches(['Templates'])"
                        :count="model.templatesCount"
                        :create-url="urls.templatesCreate"
                        @open="openUrl"
                    />
                    <StSidebarNavItem
                        v-if="model.hasFormsAccess"
                        label="Forms"
                        icon="fas fa-file-alt"
                        sub-item
                        :href="urls.forms"
                        :active="routeMatches(['Forms'])"
                        :count="model.formsCount"
                        :create-url="urls.formsCreate"
                        @open="openUrl"
                    />
                </template>
            </template>

            <template v-if="model.hasReportsAccess">
                <StSidebarNavItem
                    label="Reports"
                    icon="fal fa-chart-bar"
                    :href="urls.reports"
                    :active="routeMatches(['Reports', 'Filters'])"
                    collapsible
                    :collapsed="reportsCollapsed"
                    @toggle="reportsCollapsed = !reportsCollapsed"
                />
                <template v-if="!reportsCollapsed">
                    <StSidebarNavItem
                        v-if="model.hasReportsAdvancedAccess"
                        label="Cards"
                        icon="fas fa-th-large"
                        sub-item
                        :href="urls.cardReports"
                        :active="routeMatches(['Reports'], model.asideMenuCardReportRouteData)"
                        :count="model.cardReportsCount"
                        :create-url="urls.cardReportsCreate"
                        @open="openUrl"
                    />
                    <StSidebarNavItem
                        label="Charts"
                        icon="fas fa-chart-bar"
                        sub-item
                        :href="urls.chartReports"
                        :active="routeMatches(['Reports'], model.asideMenuChartReportRouteData)"
                        :count="model.chartReportsCount"
                        :create-url="urls.chartReportsCreate"
                        @open="openUrl"
                    />
                    <StSidebarNavItem
                        v-if="model.hasReportsAdvancedAccess"
                        label="Dashboards"
                        icon="fas fa-tachometer"
                        sub-item
                        :href="urls.dashboardReports"
                        :active="routeMatches(['Reports'], model.asideMenuDashboardReportRouteData)"
                        :count="model.dashboardReportsCount"
                        :create-url="urls.dashboardReportsCreate"
                        @open="openUrl"
                    />
                    <StSidebarNavItem
                        label="Lists / Tables"
                        icon="fas fa-list"
                        sub-item
                        :href="urls.listReports"
                        :active="routeMatches(['Reports'], model.asideMenuListReportRouteData)"
                        :count="model.listAndTableReportsCount"
                        :create-url="urls.listReportsCreate"
                        @open="openUrl"
                    />
                    <StSidebarNavItem
                        label="Matrix"
                        icon="fas fa-th"
                        sub-item
                        :href="urls.matrixReports"
                        :active="routeMatches(['Reports'], model.asideMenuMatrixReportRouteData)"
                        :count="model.matrixReportsCount"
                        :create-url="urls.matrixReportsCreate"
                        @open="openUrl"
                    />
                    <StSidebarNavItem
                        label="Views"
                        icon="fas fa-database"
                        sub-item
                        :href="urls.viewReports"
                        :active="routeMatches(['Reports'], model.asideMenuViewReportRouteData)"
                        :count="model.viewReportsCount"
                        :create-url="urls.viewReportsCreate"
                        @open="openUrl"
                    />
                    <StSidebarNavItem
                        v-if="model.hasAdvancedFiltersAccess"
                        label="Filters"
                        icon="fas fa-filter"
                        sub-item
                        :href="urls.filters"
                        :active="routeMatches(['Filters'])"
                    />
                </template>
            </template>
            <StSidebarNavItem
                v-else-if="model.hasAdvancedFiltersAccess"
                label="Filters"
                icon="fas fa-filter"
                :href="urls.filters"
                :active="routeMatches(['Filters'])"
            />

            <template
                v-if="model.hasIntegrationsAccess || model.hasRulesAccess || model.hasTranslationsAccess || model.hasWorkflowItemsAccess"
            >
                <StSidebarNavItem
                    label="Advanced"
                    icon="fal fa-cogs"
                    :href="defaultAdvancedUrl"
                    :active="
                        routeMatches(['Rules', 'Integrate', 'ChildAppIntegration', 'ExternalIntegration', 'Translations', 'WorkflowV2'])
                    "
                    collapsible
                    :collapsed="advancedCollapsed"
                    @toggle="advancedCollapsed = !advancedCollapsed"
                />
                <template v-if="!advancedCollapsed">
                    <StSidebarNavItem
                        v-if="model.hasIntegrationsAccess"
                        label="Integrate"
                        icon="fas fa-cubes"
                        sub-item
                        :href="urls.integrate"
                        :active="routeMatches(['Integrate', 'ChildAppIntegration', 'ExternalIntegration'])"
                        :count="model.integrateCount"
                        :create-url="urls.integrateCreate"
                        @open="openUrl"
                    />
                    <StSidebarNavItem
                        v-if="model.hasRulesAccess"
                        label="Rules"
                        icon="fas fa-wrench"
                        sub-item
                        :href="urls.rules"
                        :active="routeMatches(['Rules'])"
                        :count="model.rulesCount"
                        :create-url="urls.rulesCreate"
                        @open="openUrl"
                    />
                    <StSidebarNavItem
                        v-if="model.hasTranslationsAccess"
                        label="Translations"
                        icon="fas fa-globe"
                        sub-item
                        :href="urls.translations"
                        :active="routeMatches(['Translations'])"
                    />
                    <StSidebarNavItem
                        v-if="model.hasWorkflowItemsAccess"
                        label="Workflow"
                        icon="fas fa-wrench"
                        sub-item
                        :href="urls.workflow"
                        :active="routeMatches(['WorkflowV2'])"
                        :count="model.workflowV2Count"
                        :create-url="urls.workflowCreate"
                        @open="openUrl"
                    />
                </template>
            </template>

            <template v-if="model.hasAppsEditAccess">
                <StSidebarNavItem
                    label="Publish"
                    icon="fal fa-cloud"
                    :href="urls.security"
                    :active="routeMatches(['Security', 'Publish'])"
                    collapsible
                    :collapsed="publishCollapsed"
                    @toggle="publishCollapsed = !publishCollapsed"
                />
                <template v-if="!publishCollapsed">
                    <StSidebarNavItem
                        label="Security"
                        icon="fas fa-lock"
                        sub-item
                        :href="urls.security"
                        :active="routeMatches(['Security'])"
                    />
                    <StSidebarNavItem
                        label="Publish"
                        icon="fas fa-cloud"
                        sub-item
                        :href="urls.publish"
                        :active="routeMatches(['Publish'])"
                    />
                </template>
            </template>
        </template>
    </ul>
</template>
